import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Hook genérico para operações CRUD usando React Query
 * 
 * Fornece cache automático, refetch inteligente e otimistic updates
 * 
 * @param {string} queryKey - Chave única para o cache (ex: 'services', 'places')
 * @param {Object} service - Objeto com métodos getAll, create, update, delete
 * @param {Object} options - Opções adicionais
 * @returns {Object} - { items, loading, error, reload, create, update, deleteItem }
 * 
 * @example
 * const { items: services, loading, create, update, deleteItem } = useQueryCRUD(
 *   'services',
 *   servicesService
 * );
 */
export function useQueryCRUD(queryKey, service, options = {}) {
  const queryClient = useQueryClient();

  // Query para listar items
  const query = useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const data = await service.getAll();
      return data || [];
    },
    staleTime: options.staleTime || 5 * 60 * 1000, // 5 minutos
    ...options.queryOptions,
  });

  // Mutation para criar
  const createMutation = useMutation({
    mutationFn: (data) => service.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    ...options.createOptions,
  });

  // Mutation para atualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => service.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    ...options.updateOptions,
  });

  // Mutation para deletar
  const deleteMutation = useMutation({
    mutationFn: (id) => service.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    ...options.deleteOptions,
  });

  return {
    // Data
    items: query.data || [],
    
    // Loading states
    loading: query.isLoading,
    isFetching: query.isFetching,
    
    // Error
    error: query.error,
    
    // Actions
    reload: () => queryClient.invalidateQueries({ queryKey: [queryKey] }),
    
    // CRUD operations
    create: createMutation.mutate,
    createAsync: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    
    // Update aceita (id, data) OU ({ id, data })
    update: (idOrObj, data) => {
      if (typeof idOrObj === 'object' && idOrObj.id) {
        return updateMutation.mutate(idOrObj);
      }
      return updateMutation.mutate({ id: idOrObj, data });
    },
    updateAsync: (idOrObj, data) => {
      if (typeof idOrObj === 'object' && idOrObj.id) {
        return updateMutation.mutateAsync(idOrObj);
      }
      return updateMutation.mutateAsync({ id: idOrObj, data });
    },
    isUpdating: updateMutation.isPending,
    
    deleteItem: deleteMutation.mutate,
    deleteAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}

export default useQueryCRUD;
